import type { PrismaClient } from "@prisma/client";
import type { Module, RolePreset } from "../modules/types";
import { CORE_CAPABILITIES } from "./core-capabilities";
import { slugify } from "../lib/utils";
import { logger } from "../lib/logger";

// ─── Role seeding ────────────────────────────────────────────
// Called when a module is enabled for an org. Creates Role rows
// for each of the module's rolePresets. The special "owner" role
// (whose capabilities array is empty) gets expanded to receive
// every capability — core + every enabled module's.

export interface SeedContext {
  prisma: PrismaClient;
  organizationId: string;
  modules: Module[];          // all modules currently enabled for this org
  sourceModuleId: string;     // which module triggered this seed call
}

export async function seedRolesForModule(ctx: SeedContext): Promise<void> {
  const module = ctx.modules.find((m) => m.id === ctx.sourceModuleId);
  if (!module?.rolePresets?.length) return;

  const allCapabilityIds = getAllCapabilityIds(ctx.modules);

  for (const preset of module.rolePresets) {
    const effectiveCaps =
      preset.capabilities.length === 0
        ? allCapabilityIds // "owner"-style: grant everything
        : preset.capabilities;

    const slug = `${ctx.sourceModuleId}:${slugify(preset.id)}`;

    // Upsert — safe to re-run. If a role with this slug already exists
    // for this org, refresh its capabilities (in case module upgraded).
    await ctx.prisma.role.upsert({
      where: {
        organizationId_slug: {
          organizationId: ctx.organizationId,
          slug,
        },
      },
      create: {
        organizationId: ctx.organizationId,
        name: preset.name,
        slug,
        capabilities: effectiveCaps,
        isBuiltIn: true,
        sourceModuleId: ctx.sourceModuleId,
        category: preset.category,
      },
      update: {
        name: preset.name,
        capabilities: effectiveCaps,
        category: preset.category,
      },
    });
  }

  logger.info("Seeded role presets", {
    organizationId: ctx.organizationId,
    moduleId: ctx.sourceModuleId,
    presetCount: module.rolePresets.length,
  });
}

/** Find a seeded role by its canonical preset id within a module. */
export async function findPresetRole(
  prisma: PrismaClient,
  organizationId: string,
  moduleId: string,
  presetId: string,
) {
  return prisma.role.findUnique({
    where: {
      organizationId_slug: {
        organizationId,
        slug: `${moduleId}:${slugify(presetId)}`,
      },
    },
  });
}

function getAllCapabilityIds(modules: Module[]): string[] {
  const ids = new Set<string>();
  for (const c of CORE_CAPABILITIES) ids.add(c.id);
  for (const m of modules) for (const c of m.capabilities) ids.add(c.id);
  return Array.from(ids);
}
